import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Loader2, FolderOpen, AlertTriangle, RefreshCw, ExternalLink, Info, X, PanelLeftOpen, PanelLeftClose } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { toast } from 'sonner';

const IFRAME_LOAD_TIMEOUT_MS = 15000;
const BANNER_AUTO_HIDE_MS = 5000;
const NC_VISITED_KEY = 'nc_visited';

export default function FilesPage() {
    const [loading, setLoading] = useState(true);
    const [ctx, setCtx] = useState(null);
    const [iframeKey, setIframeKey] = useState(0);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [iframeTimedOut, setIframeTimedOut] = useState(false);
    // Banner is suppressed (dismissed) if user has successfully visited NC
    // before (i.e. they are likely already provisioned & logged in), OR after
    // the user manually closes it, OR after the 5s auto-hide fires.
    const [bannerDismissed, setBannerDismissed] = useState(() => {
        try { return localStorage.getItem(NC_VISITED_KEY) === '1'; } catch (e) { return false; }
    });
    const timeoutRef = useRef(null);
    const bannerHideTimerRef = useRef(null);
    const navigate = useNavigate();
    // Auto-hide app sidebar on File page (matching Mail behavior). User can
    // toggle via button; choice persists via localStorage.
    const [appSidebarHidden, setAppSidebarHidden] = useState(() => {
        const saved = localStorage.getItem('file_app_sidebar_hidden');
        return saved === null ? true : saved === 'true';
    });
    // Transient hover state — when user's cursor is near the left edge OR over
    // the (briefly expanded) sidebar, we temporarily uncollapse without
    // touching the persisted preference.
    const [sidebarHovered, setSidebarHovered] = useState(false);
    const sidebarHoverTimerRef = useRef(null);

    const effectiveCollapsed = appSidebarHidden && !sidebarHovered;

    const toggleAppSidebar = () => {
        setAppSidebarHidden(prev => {
            const next = !prev;
            localStorage.setItem('file_app_sidebar_hidden', String(next));
            return next;
        });
    };

    const handleSidebarMouseEnter = () => {
        if (sidebarHoverTimerRef.current) {
            clearTimeout(sidebarHoverTimerRef.current);
            sidebarHoverTimerRef.current = null;
        }
        setSidebarHovered(true);
    };

    const handleSidebarMouseLeave = () => {
        // Small delay prevents flicker when cursor briefly crosses the gap
        // between edge-trigger and sidebar.
        if (sidebarHoverTimerRef.current) clearTimeout(sidebarHoverTimerRef.current);
        sidebarHoverTimerRef.current = setTimeout(() => {
            setSidebarHovered(false);
        }, 150);
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/nextcloud/context');
                if (cancelled) return;
                if (!res.data?.enabled) {
                    toast.error('Fitur File belum diatur untuk perusahaan Anda');
                    navigate('/');
                    return;
                }
                setCtx(res.data);
            } catch (err) {
                if (cancelled) return;
                toast.error('Fitur File belum diatur untuk perusahaan Anda');
                navigate('/');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [navigate]);

    // Start/reset a timeout each time the iframe (re)mounts via iframeKey.
    // If onLoad hasn't fired within IFRAME_LOAD_TIMEOUT_MS, surface a
    // fallback error banner — most likely cause is X-Frame-Options blocking
    // the embed, user_oidc app misconfigured, or slow provisioning.
    useEffect(() => {
        if (!ctx?.enabled) return;
        setIframeLoaded(false);
        setIframeTimedOut(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setIframeTimedOut(true);
        }, IFRAME_LOAD_TIMEOUT_MS);
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [ctx?.enabled, iframeKey]);

    // Auto-hide the info banner after BANNER_AUTO_HIDE_MS. Timer is reset
    // whenever the banner is (re)shown via iframe refresh.
    useEffect(() => {
        if (!ctx?.enabled) return;
        if (bannerDismissed) return;
        if (bannerHideTimerRef.current) clearTimeout(bannerHideTimerRef.current);
        bannerHideTimerRef.current = setTimeout(() => {
            setBannerDismissed(true);
        }, BANNER_AUTO_HIDE_MS);
        return () => {
            if (bannerHideTimerRef.current) clearTimeout(bannerHideTimerRef.current);
        };
    }, [ctx?.enabled, bannerDismissed, iframeKey]);

    // Cleanup lingering hover timer on unmount
    useEffect(() => {
        return () => {
            if (sidebarHoverTimerRef.current) clearTimeout(sidebarHoverTimerRef.current);
        };
    }, []);

    const handleIframeLoad = () => {
        setIframeLoaded(true);
        setIframeTimedOut(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        // Mark that this user has successfully reached NC — on next visits
        // the "first-time provisioning" banner will be suppressed entirely.
        try { localStorage.setItem(NC_VISITED_KEY, '1'); } catch (e) { /* noop */ }
    };

    const handleRefresh = () => {
        // On explicit refresh, reset banner UNLESS user has been marked
        // as visited before — already-provisioned users don't need the hint.
        try {
            if (localStorage.getItem(NC_VISITED_KEY) !== '1') {
                setBannerDismissed(false);
            }
        } catch (e) { /* noop */ }
        setIframeKey(k => k + 1);
    };

    if (loading) {
        return (
            <Layout sidebarCollapsed={effectiveCollapsed} hideHeader noPadding>
                <div className="flex items-center justify-center h-screen">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    if (!ctx?.enabled) return null;

    // Build iframe URL: when provider_id is configured, use the user_oidc
    // direct-login path so Nextcloud immediately triggers SSO without showing
    // its native login page. Auto-provisioning (enabled in NC admin) handles
    // first-time account creation transparently.
    const pid = ctx.provider_id;
    const iframeSrc = pid
        ? `${ctx.nextcloud_url}/apps/user_oidc/login/${pid}`
        : ctx.nextcloud_url;

    // Banner visibility: info banner is persistent until user dismisses (iframe
    // onLoad fires too early on cross-origin targets — login page inside NC
    // would hide the banner before the user can even read it).
    // Error banner takes precedence when timeout fires.
    const showInfoBanner = !bannerDismissed && !iframeTimedOut;
    const showErrorBanner = iframeTimedOut && !bannerDismissed;

    return (
        <Layout
            sidebarCollapsed={effectiveCollapsed}
            onSidebarMouseEnter={handleSidebarMouseEnter}
            onSidebarMouseLeave={handleSidebarMouseLeave}
            hideHeader
            noPadding
        >
            {/* Invisible edge trigger — only rendered while the sidebar is
                persistently hidden. Hovering it uncollapses the sidebar
                with the same smooth animation. Sits above the iframe. */}
            {appSidebarHidden && !sidebarHovered && (
                <div
                    className="fixed left-0 top-0 h-screen w-3 z-40"
                    onMouseEnter={handleSidebarMouseEnter}
                    data-testid="sidebar-edge-trigger"
                    aria-hidden="true"
                />
            )}
            <div className="flex flex-col h-screen">
                {/* Top bar */}
                <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleAppSidebar}
                            title={appSidebarHidden ? 'Tampilkan sidebar utama' : 'Sembunyikan sidebar utama'}
                            data-testid="toggle-app-sidebar-btn"
                        >
                            {appSidebarHidden ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
                        </Button>
                        <FolderOpen className="w-5 h-5 text-brand-primary" />
                        <span className="font-semibold">File</span>
                        <span className="text-xs text-brand-text-tertiary">• {ctx.nextcloud_url}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRefresh}
                            data-testid="file-refresh-btn"
                            title="Refresh"
                        >
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(ctx.nextcloud_url, '_blank', 'noopener')}
                            data-testid="file-open-newtab-btn"
                        >
                            <ExternalLink className="w-4 h-4 mr-2" /> Buka tab baru
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/')}
                            data-testid="file-back-btn"
                        >
                            Kembali
                        </Button>
                    </div>
                </div>

                {/* Info banner - first-time provisioning hint */}
                {showInfoBanner && (
                    <div
                        className="flex items-start gap-3 px-4 py-2.5 bg-sky-50 border-b border-sky-200 text-sky-900 text-sm"
                        data-testid="nc-provisioning-banner"
                        role="status"
                    >
                        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-sky-600" />
                        <div className="flex-1">
                            Akun Nextcloud Anda akan otomatis dibuat saat login pertama. Proses login dan provisioning memerlukan beberapa detik — mohon tunggu.
                        </div>
                        <button
                            type="button"
                            onClick={() => setBannerDismissed(true)}
                            className="p-1 hover:bg-sky-100 rounded"
                            aria-label="Tutup pemberitahuan"
                            data-testid="nc-banner-dismiss-btn"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Error banner - iframe failed to load within timeout */}
                {showErrorBanner && (
                    <div
                        className="flex items-start gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-sm"
                        data-testid="nc-timeout-banner"
                        role="alert"
                    >
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                        <div className="flex-1">
                            <div className="font-medium">Nextcloud belum termuat.</div>
                            <div className="mt-0.5 text-amber-800">
                                Kemungkinan akun Anda sedang dibuat otomatis (login pertama), koneksi lambat, atau situs memblokir embed.
                                Coba <button type="button" onClick={handleRefresh} className="underline font-medium" data-testid="nc-timeout-refresh-link">muat ulang</button>
                                {' '}atau <button type="button" onClick={() => window.open(ctx.nextcloud_url, '_blank', 'noopener')} className="underline font-medium" data-testid="nc-timeout-newtab-link">buka di tab baru</button>.
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setBannerDismissed(true)}
                            className="p-1 hover:bg-amber-100 rounded"
                            aria-label="Tutup peringatan"
                            data-testid="nc-timeout-dismiss-btn"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Iframe */}
                <div className="flex-1 bg-gray-100 relative">
                    <iframe
                        key={iframeKey}
                        src={iframeSrc}
                        title="Nextcloud Files"
                        className="w-full h-full border-0"
                        allow="clipboard-read; clipboard-write; fullscreen"
                        onLoad={handleIframeLoad}
                        data-testid="nextcloud-iframe"
                    />
                </div>
            </div>
        </Layout>
    );
}
